// Steuerpaket: Import-Stand, Buchungen, Klärungen und WEG-Schlüssel je Objekt (nur Büro, RLS is_staff).
import { supabase } from '../supabase/client';
import type { JournalZeile } from './immowareJournal';
import type { Buchung, Kategorie, Klaerung } from './steuerpaket';
import type { Kostenschluessel, SchluesselArt, WegEinheit } from './weg';

export interface ImportStand {
  id: string;
  dateiname: string | null;
  zeilen: number;
  wertstellung_von: string | null;
  wertstellung_bis: string | null;
  importiert_am: string;
}

export async function ladeImportStand(objektId: string): Promise<ImportStand | null> {
  const { data, error } = await supabase.from('buchhaltung_importe')
    .select('id, dateiname, zeilen, wertstellung_von, wertstellung_bis, importiert_am')
    .eq('objekt_id', objektId).order('importiert_am', { ascending: false }).limit(1).maybeSingle();
  if (error) throw error;
  return (data as ImportStand | null) ?? null;
}

export async function importiereJournal(objektId: string, dateiname: string, zeilen: readonly JournalZeile[]): Promise<string> {
  const { data, error } = await supabase.rpc('buchhaltung_import_ersetzen', { p_objekt: objektId, p_dateiname: dateiname, p_zeilen: zeilen });
  if (error) throw error;
  return data as string;
}

const BUCHUNG_FELDER = 'zeile, nummer, text, buchungstyp, beleg_nr, buchungsdatum, wertstellung, abgrenzung, brutto, netto, ust_satz, '
  + 'soll_konto_nr, soll_konto, soll_kontoart, soll_kategorie, haben_konto_nr, haben_konto, haben_kontoart, haben_kategorie, op, op_betrag';

/** Alle Buchungen des Objekts (seitenweise – PostgREST liefert höchstens 1000 Zeilen je Abfrage). */
export async function ladeBuchungen(objektId: string): Promise<Buchung[]> {
  const alle: Buchung[] = [];
  for (let von = 0; ; von += 1000) {
    const { data, error } = await supabase.from('buchhaltung_buchungen').select(BUCHUNG_FELDER)
      .eq('objekt_id', objektId).order('id').range(von, von + 999);
    if (error) throw error;
    const zeilen = (data ?? []) as unknown as (Buchung & { brutto: number | string; netto: number | string | null; ust_satz: number | string | null; op_betrag: number | string | null })[];
    for (const z of zeilen) {
      alle.push({ ...z, text: z.text ?? '', beleg_nr: z.beleg_nr ?? '', brutto: Number(z.brutto),
        netto: z.netto == null ? null : Number(z.netto), ust_satz: z.ust_satz == null ? null : Number(z.ust_satz), op_betrag: z.op_betrag == null ? null : Number(z.op_betrag) });
    }
    if (zeilen.length < 1000) return alle;
  }
}

export interface KlaerungZeile extends Klaerung { id: string; notiz: string | null; geklaert_am: string }

export async function ladeKlaerungen(objektId: string): Promise<KlaerungZeile[]> {
  const { data, error } = await supabase.from('buchhaltung_klaerungen')
    .select('id, schluessel, aktion, kategorie, jahr, zins, notiz, geklaert_am').eq('objekt_id', objektId);
  if (error) throw error;
  return ((data ?? []) as KlaerungZeile[]).map((k) => ({ ...k, zins: k.zins == null ? null : Number(k.zins) }));
}

export async function speichereKlaerung(objektId: string, k: { schluessel: string; aktion: 'zuordnen' | 'ausschliessen'; kategorie?: Kategorie | null; jahr?: number | null; zins?: number | null; notiz?: string | null }): Promise<void> {
  const { error } = await supabase.from('buchhaltung_klaerungen').upsert({
    objekt_id: objektId, schluessel: k.schluessel, aktion: k.aktion, kategorie: k.kategorie ?? null, jahr: k.jahr ?? null,
    zins: k.zins ?? null, notiz: k.notiz?.trim() || null, geklaert_am: new Date().toISOString(),
  }, { onConflict: 'objekt_id,schluessel' });
  if (error) throw error;
}

export async function loescheKlaerung(id: string): Promise<void> {
  const { error } = await supabase.from('buchhaltung_klaerungen').delete().eq('id', id);
  if (error) throw error;
}

export async function ladeSchluessel(objektId: string): Promise<Kostenschluessel[]> {
  const { data, error } = await supabase.from('weg_kostenschluessel').select('konto, art, anteile').eq('objekt_id', objektId);
  if (error) throw error;
  return (data ?? []) as Kostenschluessel[];
}

export async function speichereSchluessel(objektId: string, konto: string, art: SchluesselArt, anteile: Record<string, number> | null): Promise<void> {
  if (art === 'mea') {
    const { error } = await supabase.from('weg_kostenschluessel').delete().eq('objekt_id', objektId).eq('konto', konto);
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from('weg_kostenschluessel').upsert(
    { objekt_id: objektId, konto, art, anteile: art === 'fest' ? anteile : null, geaendert_am: new Date().toISOString() },
    { onConflict: 'objekt_id,konto' });
  if (error) throw error;
}

/** Einheiten der WEG mit aktuellem Eigentümer und MEA (einheit_eigentuemer). */
export async function ladeWegEinheiten(objektId: string): Promise<WegEinheit[]> {
  const { data, error } = await supabase.from('objekt_einheiten')
    .select('id, bezeichnung, einheit_eigentuemer(eigentuemer_id, mea, vertrag_bis)').eq('objekt_id', objektId).order('bezeichnung');
  if (error) throw error;
  const heute = new Date().toISOString().slice(0, 10);
  return ((data ?? []) as { id: string; bezeichnung: string | null; einheit_eigentuemer: { eigentuemer_id: string; mea: number | string | null; vertrag_bis: string | null }[] | null }[])
    .map((e) => {
      const ee = (e.einheit_eigentuemer ?? []).find((x) => !x.vertrag_bis || x.vertrag_bis >= heute) ?? null;
      return { einheit_id: e.id, bezeichnung: e.bezeichnung ?? '–', eigentuemer_id: ee?.eigentuemer_id ?? null, mea: ee?.mea == null ? null : Number(ee.mea) };
    });
}

// ── Steuerpaket S4: Freigabe je Eigentümer und Jahr ─────────────────────────────
export interface FreigabeZeile { jahr: number; stand: import('./steuerpaketStand').SteuerpaketStand; freigegeben_am: string }

export async function ladeSteuerFreigabe(eigentuemerId: string, jahr: number): Promise<FreigabeZeile | null> {
  const { data, error } = await supabase.from('steuerpaket_freigaben').select('jahr, stand, freigegeben_am')
    .eq('eigentuemer_id', eigentuemerId).eq('jahr', jahr).maybeSingle();
  if (error) throw error;
  return (data as FreigabeZeile | null) ?? null;
}

export async function gibSteuerpaketFrei(eigentuemerId: string, stand: import('./steuerpaketStand').SteuerpaketStand): Promise<void> {
  const { error } = await supabase.from('steuerpaket_freigaben').upsert(
    { eigentuemer_id: eigentuemerId, jahr: stand.jahr, stand, freigegeben_am: new Date().toISOString() }, { onConflict: 'eigentuemer_id,jahr' });
  if (error) throw error;
}

export async function zieheSteuerpaketZurueck(eigentuemerId: string, jahr: number): Promise<void> {
  const { error } = await supabase.from('steuerpaket_freigaben').delete().eq('eigentuemer_id', eigentuemerId).eq('jahr', jahr);
  if (error) throw error;
}

/** Eigentümerportal: freigegebene Stände des angemeldeten Eigentümers. */
export async function ladeMeineSteuerpakete(): Promise<FreigabeZeile[]> {
  const { data, error } = await supabase.rpc('portal_meine_steuerpakete');
  if (error) throw error;
  return (data ?? []) as FreigabeZeile[];
}
